import dayjs from "dayjs";

type StandbyResumeCandidate = {
  id?: string;
  created_at?: string;
  old_fields: Record<string, unknown> | null | undefined;
  new_fields: Record<string, unknown> | null | undefined;
};

type ClosedStandbyPeriodCandidate = {
  ended_at?: string | null;
  duration_seconds?: number | null;
};

const STANDBY_STATUS = "STANDBY";

// The worklog row and the standby period are written by two separate statements, so their
// timestamps never match exactly, and the sign of the gap depends on the code path: the
// cron closes the period BEFORE writing the worklog (created_at > ended_at) while every
// other close path writes the worklog first (created_at < ended_at). The window is
// therefore symmetric, and sits well below the smallest observed gap between two
// consecutive resume events in real data (11s).
const STANDBY_MATCH_WINDOW_MS = 5_000;

// Identified from the raw status enum rather than the formatted diff labels, so that
// renaming a display string can never silently stop the durations from appearing.
export function isStandbyResumeEntry(entry: StandbyResumeCandidate): boolean {
  return (
    entry.old_fields?.status === STANDBY_STATUS &&
    entry.new_fields?.status !== STANDBY_STATUS
  );
}

function toEpochMilliseconds(timestamp: string | null | undefined): number | null {
  if (!timestamp) return null;
  const parsed = dayjs(timestamp);
  return parsed.isValid() ? parsed.valueOf() : null;
}

// Pairs each resume worklog row with the standby period that closed at the same moment,
// keyed by worklog entry id. The whole period is returned rather than just its duration,
// because the row renders the duration AND offers an edit control addressed to that
// period's id. Open periods are excluded on purpose: the ticket's Total Standby Time sums
// only closed ones, so including them here would make the per-row durations stop adding up
// to the total shown on the details page.
//
// Matching is a greedy nearest-neighbour assignment rather than an ordinal zip: a single
// resume row without a period — every row predating the standby-period table has none —
// would otherwise shift every later row onto the wrong duration. Anything left unassigned
// is simply absent from the result, because showing no duration beats showing a wrong one.
export function matchStandbyPeriodsToResumeEntries<
  Period extends ClosedStandbyPeriodCandidate,
>(
  worklogEntries: StandbyResumeCandidate[],
  standbyPeriods: Period[]
): Record<string, Period> {
  const resumeRows: Array<{ id: string; at: number }> = [];
  for (const entry of worklogEntries) {
    if (entry.id === undefined || !isStandbyResumeEntry(entry)) continue;
    const at = toEpochMilliseconds(entry.created_at);
    if (at !== null) resumeRows.push({ id: entry.id, at });
  }

  const closedPeriods: Array<{ endedAt: number; period: Period }> = [];
  for (const period of standbyPeriods) {
    if (period.duration_seconds == null) continue;
    const endedAt = toEpochMilliseconds(period.ended_at);
    if (endedAt !== null) closedPeriods.push({ endedAt, period });
  }

  const candidatePairs: Array<{ rowIndex: number; periodIndex: number; gap: number }> =
    [];
  resumeRows.forEach((row, rowIndex) => {
    closedPeriods.forEach((closedPeriod, periodIndex) => {
      const gap = Math.abs(closedPeriod.endedAt - row.at);
      if (gap <= STANDBY_MATCH_WINDOW_MS)
        candidatePairs.push({ rowIndex, periodIndex, gap });
    });
  });

  candidatePairs.sort(
    (first, second) =>
      first.gap - second.gap ||
      first.rowIndex - second.rowIndex ||
      first.periodIndex - second.periodIndex
  );

  const periodsByWorklogEntryId: Record<string, Period> = {};
  const assignedRowIndexes = new Set<number>();
  const assignedPeriodIndexes = new Set<number>();

  for (const pair of candidatePairs) {
    if (assignedRowIndexes.has(pair.rowIndex)) continue;
    if (assignedPeriodIndexes.has(pair.periodIndex)) continue;
    assignedRowIndexes.add(pair.rowIndex);
    assignedPeriodIndexes.add(pair.periodIndex);
    periodsByWorklogEntryId[resumeRows[pair.rowIndex].id] =
      closedPeriods[pair.periodIndex].period;
  }

  return periodsByWorklogEntryId;
}
